import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, BarChart3, Check, Loader2, Pencil, RefreshCw, X } from 'lucide-react';
import { BACKEND_URL } from '../../config';
import { useOrganisations } from '../../utils/organisations';
import { qrFileName, qrReportUrl } from './qr';
import { LiveStatus, QrActions, QrCodeRow, errorMessage, useLivePolling, useQrDataUrl } from './qrShared';
import QRCodeDetails from './QRCodeDetails';

/**
 * Register of the printed QR codes that open the website's report page
 * (/qr-report/org/<org>[/<authority>]?qr=<id>). Admins create a code here,
 * download or share it, browse what is out there, delete codes (soft: they
 * move to the Deleted filter and can be restored), and see each code's live
 * scan and report counts, with a detail page per code (see QRCodeDetails).
 * Deleting only hides the code in this list: a printed copy keeps working and
 * is still counted. `stale` flags codes whose organisation or authority is gone.
 */

type AuthorityOption = { id: number; name: string };

type StatusFilter = 'active' | 'deleted' | 'all';

const LABEL_MAX = 120;
const PLACEMENT_MAX = 255;

// Same field language as the Accounts / Organisations tabs.
const labelCls = 'block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5 text-left';
const inputCls =
	'w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-900 bg-white focus:border-blue-400 focus:outline-none';

/** Active authorities of one organisation, from the same public endpoint the QR page uses. */
function useAuthorities(orgKey: string): { authorities: AuthorityOption[]; loading: boolean } {
	const [authorities, setAuthorities] = useState<AuthorityOption[]>([]);
	const [loading, setLoading] = useState(false);
	useEffect(() => {
		setAuthorities([]);
		if (!orgKey) return;
		const ctrl = new AbortController();
		setLoading(true);
		fetch(`${BACKEND_URL}/api/organisations/${encodeURIComponent(orgKey)}/authorities`, { signal: ctrl.signal })
			.then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
			.then((data) => setAuthorities(Array.isArray(data?.authorities) ? data.authorities : []))
			.catch((err) => {
				if (err?.name !== 'AbortError') console.error('Could not load authorities', err);
			})
			.finally(() => setLoading(false));
		return () => ctrl.abort();
	}, [orgKey]);
	return { authorities, loading };
}

const Modal = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
	<div className='fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4'>
		<div className='bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md p-6 text-left'>
			<div className='flex items-center justify-between gap-2 mb-5'>
				<h2 className='text-xl font-extrabold text-blue-900 m-0'>{title}</h2>
				<button onClick={onClose} className='text-gray-400 hover:text-gray-700'>
					<X className='w-5 h-5' />
				</button>
			</div>
			{children}
		</div>
	</div>
);

const EditModal = ({ qr, onClose, onSaved }: { qr: QrCodeRow; onClose: () => void; onSaved: () => void }) => {
	const [label, setLabel] = useState(qr.label);
	const [placement, setPlacement] = useState(qr.placement ?? '');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const save = async () => {
		if (!label.trim()) {
			setError('Label is required.');
			return;
		}
		setSaving(true);
		setError(null);
		try {
			const res = await fetch(`${BACKEND_URL}/users/qr/${qr.id}`, {
				method: 'PUT',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ label, placement }),
			});
			if (!res.ok) {
				setError(await errorMessage(res));
				return;
			}
			onSaved();
		} catch {
			setError('Could not reach the server.');
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal title='Edit QR code' onClose={onClose}>
			<div className='space-y-4'>
				<div>
					<label className={labelCls}>Label</label>
					<input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={LABEL_MAX} className={inputCls} />
				</div>
				<div>
					<label className={labelCls}>Placement</label>
					<input
						value={placement}
						onChange={(e) => setPlacement(e.target.value)}
						maxLength={PLACEMENT_MAX}
						className={inputCls}
					/>
				</div>
				<p className='text-xs text-gray-500 m-0'>
					The organisation and authority can't change: they're already printed. Create a new code to point somewhere
					else.
				</p>
			</div>
			<div className='flex items-center gap-3 mt-6'>
				{error && <p className='text-sm font-semibold text-red-600 m-0'>{error}</p>}
				<button
					onClick={save}
					disabled={saving}
					className='ml-auto inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 h-9 font-bold text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400'
				>
					{saving ? <Loader2 className='w-4 h-4 animate-spin' /> : <Check className='w-4 h-4' />}
					Save
				</button>
			</div>
		</Modal>
	);
};

const QrCard = ({
	qr,
	onEdit,
	onToggleActive,
	onOpen,
	busy,
}: {
	qr: QrCodeRow;
	onEdit: () => void;
	onToggleActive: () => void;
	onOpen: () => void;
	busy: boolean;
}) => {
	const url = qrReportUrl(qr.orgKey, qr.authorityName, qr.id);
	const dataUrl = useQrDataUrl(url, 512);

	return (
		<div
			className={`bg-white p-4 rounded-xl border shadow-sm flex gap-4 text-left ${
				qr.isActive ? 'border-gray-200 hover:border-blue-300 hover:shadow-md' : 'border-gray-200 opacity-60'
			} transition-all`}
		>
			<div className='w-28 h-28 shrink-0 rounded-lg border border-gray-100 bg-white flex items-center justify-center'>
				{dataUrl ? (
					<img src={dataUrl} alt={`QR for ${qr.label}`} className='w-full h-full' />
				) : (
					<Loader2 className='w-6 h-6 animate-spin text-gray-300' />
				)}
			</div>
			<div className='flex-1 min-w-0 flex flex-col gap-1.5'>
				<div className='flex items-start justify-between gap-2'>
					<h3 className='text-lg font-bold text-gray-900 truncate m-0' title={qr.label}>
						{qr.label}
					</h3>
					<div className='flex items-center gap-1 shrink-0'>
						<button
							onClick={onEdit}
							title='Edit'
							className='p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700'
						>
							<Pencil className='w-4 h-4' />
						</button>
						<button
							onClick={onToggleActive}
							disabled={busy}
							className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-bold disabled:opacity-50 ${
								qr.isActive ? 'text-red-600 hover:bg-red-50' : 'text-blue-700 hover:bg-blue-50'
							}`}
						>
							{busy && <Loader2 className='w-3.5 h-3.5 animate-spin' />}
							{qr.isActive ? 'Delete' : 'Restore'}
						</button>
					</div>
				</div>
				<div className='flex flex-wrap items-center gap-1.5'>
					<span className='bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide'>
						{qr.orgKey}
					</span>
					<span className='bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide'>
						{qr.authorityName ?? (qr.authorityId ? 'Unknown authority' : 'Any authority')}
					</span>
					{!qr.isActive && (
						<span className='bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide'>
							Deleted
						</span>
					)}
					{qr.stale && (
						<span
							title={qr.staleReason ?? undefined}
							className='inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide'
						>
							<AlertTriangle className='w-3 h-3' /> {qr.staleReason}
						</span>
					)}
				</div>
				<p className='text-sm text-gray-500 m-0 truncate'>
					{qr.placement || <span className='italic text-gray-400'>No placement recorded</span>}
					<span className='mx-1.5 text-gray-300'>•</span>
					<span className='text-[12px] text-gray-400'>
						{new Date(qr.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
					</span>
				</p>
				<button
					onClick={onOpen}
					title='Scans and reports'
					className='self-start inline-flex items-center gap-3 rounded-lg bg-gray-50 hover:bg-blue-50 px-2.5 py-1 text-xs text-gray-600 hover:text-blue-800 transition-colors'
				>
					<BarChart3 className='w-3.5 h-3.5' />
					<span>
						<b className='tabular-nums text-gray-900'>{qr.scanCount}</b> {qr.scanCount === 1 ? 'scan' : 'scans'}
					</span>
					<span>
						<b className='tabular-nums text-gray-900'>{qr.uniqueScanners}</b> unique
					</span>
					<span>
						<b className='tabular-nums text-gray-900'>{qr.reportCount}</b> {qr.reportCount === 1 ? 'report' : 'reports'}
					</span>
					<span className='font-bold text-blue-700'>Analytics →</span>
				</button>
				<div className='mt-auto pt-1'>
					<QrActions url={url} fileName={qrFileName(qr.orgKey, qr.label)} title={qr.label} />
				</div>
			</div>
		</div>
	);
};

const QRCodes = () => {
	const orgs = useOrganisations();

	// ── Create form ──
	const [label, setLabel] = useState('');
	const [placement, setPlacement] = useState('');
	const [orgKey, setOrgKey] = useState('');
	const [authorityId, setAuthorityId] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);
	const [created, setCreated] = useState<string | null>(null);
	const { authorities, loading: authoritiesLoading } = useAuthorities(orgKey);

	const selectedAuthority = authorities.find((a) => String(a.id) === authorityId) ?? null;
	const previewUrl = orgKey ? qrReportUrl(orgKey, selectedAuthority?.name) : null;
	const previewDataUrl = useQrDataUrl(previewUrl, 512);

	// ── List ──
	const [codes, setCodes] = useState<QrCodeRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [listError, setListError] = useState<string | null>(null);
	const [filter, setFilter] = useState('');
	const [orgFilter, setOrgFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
	const [editing, setEditing] = useState<QrCodeRow | null>(null);
	const [busyId, setBusyId] = useState<number | null>(null);
	const [viewing, setViewing] = useState<QrCodeRow | null>(null);
	const [updatedAt, setUpdatedAt] = useState<number | null>(null);
	const [refreshFailed, setRefreshFailed] = useState(false);
	const inFlight = useRef(false);

	// `silent` is the background refresh: no spinner and no error banner, so
	// the cards don't flicker every 15 seconds and a network blip never wipes
	// the numbers on screen. A failure only flips the small status label.
	const loadList = useCallback(async (silent = false) => {
		if (inFlight.current) return;
		inFlight.current = true;
		if (!silent) {
			setLoading(true);
			setListError(null);
		}
		try {
			const res = await fetch(`${BACKEND_URL}/users/qr/list`, { credentials: 'include' });
			if (!res.ok) {
				if (silent) setRefreshFailed(true);
				else setListError(await errorMessage(res));
				return;
			}
			const data = await res.json();
			setCodes(Array.isArray(data) ? data : []);
			setUpdatedAt(Date.now());
			setRefreshFailed(false);
		} catch {
			if (silent) setRefreshFailed(true);
			else setListError('Could not reach the server.');
		} finally {
			inFlight.current = false;
			if (!silent) setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadList();
	}, [loadList]);

	// Live numbers: refresh quietly while this tab is open and the list (not a
	// code's detail page, which polls for itself) is showing.
	useLivePolling(() => loadList(true), !viewing);

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!label.trim()) {
			setFormError('Give the code a label.');
			return;
		}
		if (!orgKey) {
			setFormError('Pick an organisation.');
			return;
		}
		setSubmitting(true);
		setFormError(null);
		try {
			const res = await fetch(`${BACKEND_URL}/users/qr`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ label, placement, orgKey, authorityId: authorityId ? Number(authorityId) : null }),
			});
			if (!res.ok) {
				setFormError(await errorMessage(res));
				return;
			}
			setCreated(label.trim());
			setTimeout(() => setCreated(null), 2500);
			setLabel('');
			setPlacement('');
			loadList();
		} catch {
			setFormError('Could not reach the server.');
		} finally {
			setSubmitting(false);
		}
	};

	const toggleActive = async (qr: QrCodeRow) => {
		if (
			qr.isActive &&
			!window.confirm(
				`Delete "${qr.label}"?\n\nIt moves to the Deleted filter and can be restored. Printed copies keep working, and their scans and reports are still counted.`
			)
		)
			return;
		setBusyId(qr.id);
		try {
			const res = await fetch(`${BACKEND_URL}/users/qr/${qr.id}`, {
				method: 'PUT',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ isActive: !qr.isActive }),
			});
			if (!res.ok) {
				setListError(await errorMessage(res));
				return;
			}
			setCodes((list) => list.map((c) => (c.id === qr.id ? { ...c, isActive: !qr.isActive } : c)));
		} catch {
			setListError('Could not reach the server.');
		} finally {
			setBusyId(null);
		}
	};

	const visibleCodes = useMemo(() => {
		const q = filter.trim().toLowerCase();
		return codes.filter((c) => {
			if (statusFilter === 'active' && !c.isActive) return false;
			if (statusFilter === 'deleted' && c.isActive) return false;
			if (orgFilter && c.orgKey !== orgFilter) return false;
			if (!q) return true;
			return [c.label, c.placement, c.authorityName, c.orgKey, c.orgFullName].some((f) => f?.toLowerCase().includes(q));
		});
	}, [codes, filter, orgFilter, statusFilter]);

	const staleCount = codes.filter((c) => c.isActive && c.stale).length;

	if (viewing) {
		return (
			<QRCodeDetails
				qr={viewing}
				onBack={() => {
					setViewing(null);
					loadList();
				}}
			/>
		);
	}

	return (
		<div className='max-w-6xl mx-auto px-4 pb-5'>
			<h1 className='text-3xl font-extrabold text-blue-900 mb-8'>QR Codes</h1>

			{/* Create */}
			<form onSubmit={submit} className='bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8 text-left'>
				<h2 className='text-xs font-bold uppercase tracking-wide text-gray-500 m-0 mb-5'>Create QR code</h2>

				<div className='flex gap-8'>
					<div className='flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 content-start'>
						<div>
							<label className={labelCls}>Label</label>
							<input
								value={label}
								onChange={(e) => setLabel(e.target.value)}
								maxLength={LABEL_MAX}
								placeholder='e.g. Main gate poster'
								className={inputCls}
							/>
						</div>
						<div>
							<label className={labelCls}>
								Placement <span className='normal-case font-semibold text-gray-400'>(optional)</span>
							</label>
							<input
								value={placement}
								onChange={(e) => setPlacement(e.target.value)}
								maxLength={PLACEMENT_MAX}
								placeholder='e.g. Hostel 3 notice board'
								className={inputCls}
							/>
						</div>
						<div>
							<label className={labelCls}>Organisation</label>
							<select
								value={orgKey}
								onChange={(e) => {
									setOrgKey(e.target.value);
									setAuthorityId('');
								}}
								className={inputCls}
							>
								<option value=''>Select…</option>
								{orgs.map((o) => (
									<option key={o.key} value={o.key}>
										{o.fullName} ({o.key})
									</option>
								))}
							</select>
						</div>
						<div>
							<label className={labelCls}>Authority</label>
							<select
								value={authorityId}
								onChange={(e) => setAuthorityId(e.target.value)}
								disabled={!orgKey || authoritiesLoading}
								className={`${inputCls} disabled:bg-gray-50 disabled:text-gray-400`}
							>
								<option value=''>{authoritiesLoading ? 'Loading…' : 'Any (reporter picks)'}</option>
								{authorities.map((a) => (
									<option key={a.id} value={a.id}>
										{a.name}
									</option>
								))}
							</select>
						</div>
						{previewUrl && (
							<div className='md:col-span-2'>
								<label className={labelCls}>Opens</label>
								<p className='font-mono text-xs text-gray-600 break-all m-0'>
									{previewUrl}
									<span className='text-gray-400'>?qr=&lt;id&gt;</span>
								</p>
							</div>
						)}
					</div>

					{/* Look-only preview. The printable code carries its id (?qr=), which
					    exists only after Create, so download/print live on the saved card. */}
					<div className='w-44 shrink-0 flex flex-col items-center gap-2'>
						<div className='w-44 h-44 rounded-xl border border-dashed border-gray-200 flex items-center justify-center bg-gray-50'>
							{previewDataUrl ? (
								<img src={previewDataUrl} alt='QR preview' className='w-full h-full rounded-xl' />
							) : (
								<span className='text-xs text-gray-400 px-4 text-center'>Pick an organisation to preview</span>
							)}
						</div>
						{previewDataUrl && (
							<span className='text-[11px] text-gray-400 text-center leading-tight'>
								Preview only. Download from the saved code below so its scans are counted.
							</span>
						)}
					</div>
				</div>

				<div className='flex items-center gap-3 mt-6'>
					{formError && <p className='text-sm font-semibold text-red-600 m-0'>{formError}</p>}
					{created && (
						<p className='text-sm font-semibold text-emerald-700 m-0'>Created "{created}". Find it below to download or share.</p>
					)}
					<button
						type='submit'
						disabled={submitting}
						className='ml-auto inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 h-9 font-bold text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400'
					>
						{submitting ? <Loader2 className='w-4 h-4 animate-spin' /> : <Check className='w-4 h-4' />}
						Create
					</button>
				</div>
			</form>

			{/* Browse */}
			<div className='bg-white p-2 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-2 mb-6'>
				<input
					type='text'
					className='flex-1 w-full pl-4 pr-3 py-1 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-gray-700 font-semibold h-[34px] text-sm'
					placeholder='Search by label, placement or authority...'
					value={filter}
					onChange={(e) => setFilter(e.target.value)}
				/>
				<select
					value={orgFilter}
					onChange={(e) => setOrgFilter(e.target.value)}
					className='h-[34px] rounded-xl border border-gray-200 px-2 text-sm font-semibold text-gray-700 bg-white'
				>
					<option value=''>All organisations</option>
					{orgs.map((o) => (
						<option key={o.key} value={o.key}>
							{o.key}
						</option>
					))}
				</select>
				<select
					value={statusFilter}
					onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
					className='h-[34px] rounded-xl border border-gray-200 px-2 text-sm font-semibold text-gray-700 bg-white'
				>
					<option value='active'>Active</option>
					<option value='deleted'>Deleted</option>
					<option value='all'>All</option>
				</select>
				<div className='w-px h-6 bg-gray-200 mx-1'></div>
				<button
					className='bg-blue-600 hover:bg-blue-700 text-white px-3 rounded-xl font-bold transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2 h-[34px] text-sm w-fit sm:w-[100px]'
					onClick={() => loadList()}
					disabled={loading}
				>
					{loading ? (
						<Loader2 className='w-4 h-4 animate-spin' />
					) : (
						<>
							<RefreshCw className='w-4 h-4' /> <span className='hidden sm:inline'>Refresh</span>
						</>
					)}
				</button>
			</div>

			<div className='mb-4 -mt-3 px-1 text-left min-h-[1rem]'>
				<LiveStatus updatedAt={updatedAt} failed={refreshFailed} />
			</div>

			{staleCount > 0 && (
				<div className='mb-4 py-2 px-4 flex items-center gap-2 rounded-xl bg-amber-50 text-amber-800 border border-amber-100 text-sm font-semibold text-left'>
					<AlertTriangle className='w-4 h-4 shrink-0' />
					{staleCount} active {staleCount === 1 ? 'code points' : 'codes point'} at an organisation or authority that no
					longer exists. Scanning {staleCount === 1 ? 'it' : 'them'} dead-ends; replace and delete.
				</div>
			)}

			{listError && (
				<div className='mt-2 py-1.5 px-4 flex items-center justify-center rounded-xl bg-red-50 text-red-600 border border-red-100'>
					<p className='font-semibold text-center m-0 leading-none'>{listError}</p>
				</div>
			)}

			<div className='mt-4'>
				{loading && codes.length === 0 ? (
					<div className='flex flex-col items-center justify-center py-20 opacity-50'>
						<Loader2 className='w-12 h-12 animate-spin text-blue-600 mb-4' />
					</div>
				) : visibleCodes.length === 0 ? (
					<p className='text-gray-500 font-medium py-10 m-0'>
						{codes.length === 0 ? 'No QR codes yet.' : 'No QR codes match these filters.'}
					</p>
				) : (
					<div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
						{visibleCodes.map((qr) => (
							<QrCard
								key={qr.id}
								qr={qr}
								onEdit={() => setEditing(qr)}
								onToggleActive={() => toggleActive(qr)}
								onOpen={() => setViewing(qr)}
								busy={busyId === qr.id}
							/>
						))}
					</div>
				)}
			</div>

			{editing && (
				<EditModal
					qr={editing}
					onClose={() => setEditing(null)}
					onSaved={() => {
						setEditing(null);
						loadList();
					}}
				/>
			)}
		</div>
	);
};

export default QRCodes;
